"use client";

import { motion } from "src/lib/motion";
import { Check } from "lucide-react";
import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import { Icons } from "@/components/icons";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import visonImage from "@/assets/images/vision_about.svg";
import missionImage from "@/assets/images/mission_about.svg";
import valuesImage from "@/assets/images/values_about.svg";
import { useNextSectionScroll } from "@/lib/hooks/use-next-section-scroll";
import { useTranslations } from "@/lib/hooks/use-translations";

interface SectionProps {
  locale: string
  title: string;
  description: string;
  points: string[];
  image: StaticImageData;
  imageAlt: string;
  isReversed?: boolean;
  sectionId: string;
  contactUrl: string;
}

function Section({ locale, title, description, points, image, imageAlt, isReversed = false, sectionId, contactUrl }: SectionProps) {
  const handleNextSection = useNextSectionScroll(sectionId);
  const { t } = useTranslations();

  // Ensure points is an array
  const pointsArray = Array.isArray(points) ? points : [];

  return (
    <div id={sectionId} className="grid md:grid-cols-2 gap-6 md:gap-12 items-center max-md:pb-4 max-md:!mt-16">
      <div className={`space-y-6 max-md:row-start-2 ${isReversed ? ' md:col-start-2' : ''}`}>
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-3xl font-bold"
        >
          {title}
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-muted-foreground"
        >
          {description}
        </motion.p>
        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="space-y-3"
        >
          {pointsArray.map((point, index) => (
            <li key={index} className="flex items-center gap-3">
              <div className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Check className="h-4 w-4 text-primary" />
              </div>
              <span>{point}</span>
            </li>
          ))}
        </motion.ul>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row gap-4 pt-6"
        >
          <Link
            href={contactUrl}
            className={cn(
              buttonVariants({ variant: "default" }),
              "w-full sm:w-auto flex gap-2"
            )}
          >
            <Icons.logoIcon className="h-6 w-6" />
            {t('pages.about.buttons.getStarted')}
          </Link>
          <button
            onClick={title === t('pages.about.values.title')
              ? () => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" })
              : handleNextSection
            }
            className={cn(
              buttonVariants({ variant: "outline" }),
              "w-full sm:w-auto"
            )}
          >
            {t('pages.about.buttons.readMore')}
          </button>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0, x: isReversed ? -20 : 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        viewport={{ once: true }}
        className={`relative ${isReversed ? 'md:col-start-1 md:row-start-1' : ''}`}
      >
        <div className="aspect-[4/3] rounded-lg overflow-hidden">
          <Image
            src={image}
            alt={imageAlt}
            className="w-full h-full object-cover"
            width={800}
            height={600}
          />
        </div>
        <div className="absolute inset-0 rounded-lg bg-gradient-to-tr from-primary/20 to-transparent opacity-60"></div>
      </motion.div>
    </div>
  );
}

export default function VisionMissionSection({ locale = "en" }: { locale?: "de" | "en" | "zh-CN" } = {}) {
  const { t, locale: currentLocale } = useTranslations();

  const getContactUrl = () => {
    const localeToUse = currentLocale || locale;
    if (localeToUse === "de") return "/de/kontakt";
    if (localeToUse === "zh-CN") return "/zh-CN/contact";
    return "/en/contact";
  };

  const sections = [
    {
      title: t('pages.about.vision.title'),
      description: t('pages.about.vision.description'),
      points: t<string[]>('pages.about.vision.points'),
      image: visonImage,
      imageAlt: "Interactive business dashboard featuring charts, maps, and data visualizations for KPI and performance analytics."
    },
    {
      title: t('pages.about.mission.title'),
      description: t('pages.about.mission.description'),
      points: t<string[]>('pages.about.mission.points'),
      image: missionImage,
      imageAlt: "Big data analytics illustration showing interconnected data nodes, cloud computing, and AI-driven technology for processing large-scale information"
    },
    {
      title: t('pages.about.values.title'),
      description: t('pages.about.values.description'),
      points: t<string[]>('pages.about.values.points'),
      image: valuesImage,
      imageAlt: "Modern data visualization concept featuring charts, graphs, and UI elements for presenting insights in a digital dashboard interface."
    }
  ];

  return (
    <section className="max-md:pt-20 pt-12 pb-24 bg-neutral-100 dark:bg-neutral-900">
      <div className="container mx-auto px-6">
        <div className="space-y-20 max-w-5xl mx-auto">
          {sections.map((section, index) => (
            <Section
              locale = {locale}
              key={index}
              title={section.title}
              description={section.description}
              points={section.points}
              image={section.image}
              imageAlt={section.imageAlt}
              isReversed={index % 2 !== 0}
              sectionId={`vision-mission-${index + 1}`}
              contactUrl={getContactUrl()}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
